import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useShowAlert } from 'hooks';
import API from 'services/API';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import CandidateForm from 'UI/components/organisms/CandidateForm';
import { FormFieldsMap } from 'UI/components/organisms/CandidateForm/fields';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage } from 'UI/utils';
import { preloadFromBackend, prepareFormToSubmit } from 'UI/utils/forms';

import { formatPhoneNumbers, PHONE_FORMAT_MODES } from '../PhoneNumbersForm/utils';

/**
 * @param {Object} props
 * @param {*} props.candidate
 * @param {Function} props.onEditCompleted
 * @param {Function} props.onEditClosed
 */
const CandidateEdit = ({ candidate, onEditCompleted, onEditClosed }) => {
  const defaultValues = preloadFromBackend(candidate, FormFieldsMap);

  const form = useForm({
    defaultValues
  });

  const { showAlert } = useShowAlert();

  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false,
    isFormDisabled: false,
    isReadOnly: false
  });

  const onSubmit = async formData => {
    try {
      setUiState(prevState => ({ ...prevState, isSaving: true }));
      const transformedData = prepareFormToSubmit(formData, FormFieldsMap);
      const preparedPhoneNumbers = formatPhoneNumbers(formData, PHONE_FORMAT_MODES.submit);
      const candidateData = {
        ...transformedData,
        phones: preparedPhoneNumbers
      };
      const response = await API.put(`${Endpoints.Candidates}/${candidate.id}`, candidateData);

      if (response.data && response.status === HTTPStatusCodes.Created) {
        showAlert({
          severity: 'success',
          title: strings.inventoryProfiles.sections.candidates.candidateEdit,
          body: response.data?.personalInformation?.full_name
        });

        const updatedProfile = response.data;
        onEditCompleted(updatedProfile);
      }
    } catch (err) {
      showAlert({
        severity: 'error',
        title: 'Candidate',
        body: getErrorMessage(err)
      });
    }
    setUiState(prevState => ({
      ...prevState,
      isSaving: false,
      isSuccess: false
    }));
  };

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose: onEditClosed,
    isSaveDisabled: uiState.isSaving,
    isSaving: uiState.isSaving
  });

  return (
    <FPDrawer
      FPDrawerProps={{ open: true, onClose: onEditClosed }}
      CardHeaderProps={{
        title: strings.inventoryProfiles.common.editProfile,
        subheader: candidate?.personalInformation?.full_name
      }}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: form.handleSubmit(onSubmit) }}
      onClose={onEditClosed}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <FormContext {...form}>
        <CandidateForm isEditing />
      </FormContext>
    </FPDrawer>
  );
};

export default CandidateEdit;
